export type RequestedParameterDestination = 'project' | 'family';
export type RequestedParameterBindingKind = 'type' | 'instance';

export interface RequestedParameterIntentInput {
  name: string;
  destination: RequestedParameterDestination;
  bindingKind: RequestedParameterBindingKind;
}

export interface RequestedParameterIntentGroup {
  parameterNames?: readonly string[] | null;
  destination?: RequestedParameterDestination | null;
  bindingKind?: RequestedParameterBindingKind | null;
}

interface RequestedParameterIntentLike {
  name?: unknown;
  destination?: unknown;
  bindingKind?: unknown;
}

export function normalizeRequestedParameterIntents(
  intents?: readonly RequestedParameterIntentLike[]
): RequestedParameterIntentInput[] {
  const normalized = new Map<string, RequestedParameterIntentInput>();
  (intents ?? []).forEach((intent) => {
    const name = typeof intent.name === 'string' ? intent.name.trim() : '';
    const destination = typeof intent.destination === 'string'
      ? intent.destination.trim().toLowerCase()
      : '';
    const bindingKind = typeof intent.bindingKind === 'string'
      ? intent.bindingKind.trim().toLowerCase()
      : '';
    if (
      !name ||
      (destination !== 'project' && destination !== 'family') ||
      (bindingKind !== 'type' && bindingKind !== 'instance')
    ) {
      return;
    }

    const value: RequestedParameterIntentInput = {
      name,
      destination,
      bindingKind,
    };
    normalized.set(
      `${name.toLowerCase()}|${destination}|${bindingKind}`,
      value
    );
  });

  return Array.from(normalized.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([, intent]) => intent);
}

export function buildRequestedParameterIntents(
  groups: readonly RequestedParameterIntentGroup[]
): RequestedParameterIntentInput[] {
  return normalizeRequestedParameterIntents(groups.flatMap((group) =>
    (group.parameterNames ?? []).map((name) => ({
      name,
      destination: group.destination,
      bindingKind: group.bindingKind,
    }))
  ));
}

export function buildRequestedParameterIntentSignature(
  intents?: readonly RequestedParameterIntentLike[]
): string {
  const normalized = normalizeRequestedParameterIntents(intents);
  return normalized.length === 0
    ? 'legacy'
    : normalized
        .map((intent) =>
          `${intent.name.toLowerCase()}:${intent.destination}:${intent.bindingKind}`
        )
        .join('|');
}

export function filterRequestedParameterIntents(
  intents: readonly RequestedParameterIntentInput[],
  parameterNames: readonly string[]
): RequestedParameterIntentInput[] {
  const requestedNames = new Set(
    parameterNames.map((name) => name.trim().toLowerCase()).filter(Boolean)
  );
  return normalizeRequestedParameterIntents(intents).filter((intent) =>
    requestedNames.has(intent.name.toLowerCase())
  );
}

export function parseAuthoritativeModelVersion(value: unknown): number | undefined {
  const parsed = typeof value === 'bigint' ? Number(value) : Number(value);
  // Proto3 decoders surface an absent scalar from an older plugin as zero.
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

export function replaceCacheScopeModelVersion(
  cacheScopeKey: string,
  modelVersion: number | undefined
): string {
  if (typeof modelVersion !== 'number') return cacheScopeKey;
  return /::v(?:\d+|unknown)(?=::|$)/i.test(cacheScopeKey)
    ? cacheScopeKey.replace(/::v(?:\d+|unknown)(?=::|$)/i, `::v${modelVersion}`)
    : `${cacheScopeKey}::v${modelVersion}`;
}

export function readCacheScopeModelVersion(cacheScopeKey?: string | null): number | undefined {
  const match = cacheScopeKey?.match(/::v(\d+)(?=::|$)/i);
  return match ? parseAuthoritativeModelVersion(match[1]) : undefined;
}

function readCacheScopeIdentity(cacheScopeKey?: string | null): string {
  return (cacheScopeKey ?? '')
    .replace(/::v(?:\d+|unknown)(?=::|$)/i, '')
    .trim()
    .toLowerCase();
}

export function selectAuthoritativeCacheScope(
  notifiedScope: string,
  responseScope?: string | null
): string {
  if (
    !responseScope ||
    readCacheScopeIdentity(notifiedScope) !== readCacheScopeIdentity(responseScope)
  ) {
    return notifiedScope;
  }

  const notifiedVersion = readCacheScopeModelVersion(notifiedScope) ?? -1;
  const responseVersion = readCacheScopeModelVersion(responseScope) ?? -1;
  return responseVersion > notifiedVersion
    ? replaceCacheScopeModelVersion(notifiedScope, responseVersion)
    : notifiedScope;
}

export function appendRequestedParameterIntentCacheIdentity(
  cacheScopeKey: string,
  intents?: readonly RequestedParameterIntentLike[]
): string {
  const baseScope = cacheScopeKey.replace(/::intent:.*$/i, '');
  const signature = buildRequestedParameterIntentSignature(intents);
  return signature === 'legacy'
    ? baseScope
    : `${baseScope}::intent:${signature}`;
}
